import React, { useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertCircle, Upload, FileText, X } from 'lucide-react';
import { Button } from '../ui/button';

const Step9Certification = ({ formData, handleChange }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid file (PDF, DOC, DOCX, or TXT)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Please select a file smaller than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange('resume_data', reader.result);
      handleChange('resume_filename', file.name);
      handleChange('resume_content_type', file.type);
    };
    reader.readAsDataURL(file);
  };

  const removeResume = () => {
    handleChange('resume_data', '');
    handleChange('resume_filename', '');
    handleChange('resume_content_type', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Applicant Acknowledgment & Certification</h3>
        <p className="text-gray-600">Please read carefully before signing</p>
      </div>

      {/* Resume Upload Section */}
      <div className="bg-amber-50 border-2 border-amber-200 p-6 rounded-lg">
        <div className="flex gap-3 mb-4">
          <FileText className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <h4 className="font-bold text-lg text-amber-900">Resume Upload (Optional)</h4>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          Upload your resume to help us learn more about your experience. Accepted formats: PDF, DOC, DOCX, TXT (max 5MB)
        </p>
        
        {formData.resume_filename ? (
          <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-amber-200">
            <FileText className="w-5 h-5 text-amber-600" />
            <span className="flex-1 text-sm font-medium text-gray-800">{formData.resume_filename}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={removeResume}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-amber-300 rounded-lg p-6 text-center hover:border-amber-500 transition-colors cursor-pointer bg-white"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-10 h-10 text-amber-400 mx-auto mb-2" />
            <p className="text-gray-600">Click to upload your resume</p>
            <p className="text-sm text-gray-400">PDF, DOC, DOCX, or TXT up to 5MB</p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      <div className="bg-blue-50 border-2 border-blue-200 p-6 rounded-lg">
        <div className="flex gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
          <h4 className="font-bold text-lg text-blue-900">Important - Please Read</h4>
        </div>
        <div className="space-y-4 text-sm text-gray-800 leading-relaxed">
          <p>
            I certify that the information provided in this application is <strong>true, complete, and accurate</strong> to the best of my knowledge. I understand that any false statements, omissions, or misrepresentations may result in disqualification from consideration or, if hired, disciplinary action up to and including termination of employment.
          </p>
          <p>
            I understand that employment, if offered, is <strong>at-will</strong>, meaning that either the employer or I may terminate the employment relationship at any time, with or without cause or notice, in accordance with applicable law.
          </p>
          <p>
            I authorize the Company to verify the information provided in this application, including employment history, education, references, and background checks, as permitted by law. I understand that employment may be contingent upon meeting all applicable pre-employment requirements.
          </p>
        </div>
      </div>

      <div className="bg-white border-2 border-gray-300 p-6 rounded-lg space-y-6">
        <div>
          <Label htmlFor="certification_signature">Applicant Name (Print - Type your full name) *</Label>
          <Input
            id="certification_signature"
            value={formData.certification_signature}
            onChange={(e) => handleChange('certification_signature', e.target.value)}
            placeholder="Type your full legal name"
            required
            className="text-lg font-semibold"
          />
          <p className="text-xs text-gray-600 mt-1">
            By typing your name above, you are providing your electronic signature and agree to all terms stated.
          </p>
        </div>

        <div>
          <Label htmlFor="certification_date">Date *</Label>
          <Input
            id="certification_date"
            type="date"
            value={formData.certification_date}
            onChange={(e) => handleChange('certification_date', e.target.value)}
            required
          />
        </div>

        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>✓ Ready to Submit:</strong> By clicking "Submit Application" below, you certify that all information provided is accurate and you have read and agree to the acknowledgment above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step9Certification;